import authService from './authService';

// Configuration de l'URL de base pour les verrous
// En production, utilise une URL relative, en développement utilise localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api'
);

// Debug: afficher l'URL utilisée pour les verrous (à supprimer en production)
// console.log('LockService API_BASE_URL:', API_BASE_URL);

export interface LockStatus {
  locked: boolean;
  lockedBy?: string;
  userId?: string;
  lockedAt?: string;
  expiresAt?: string;
}

export interface LockResult {
  success: boolean;
  message: string;
  expiresAt?: string;
}

export interface LockError {
  type: string;
  message: string;
  lockedBy?: string;
  lockedAt?: string;
  expiresAt?: string;
}

class LockService {
  // Acquérir un verrou sur un site web
  async acquireLock(websiteId: string): Promise<LockResult> {
    try {
      const response = await fetch(`${API_BASE_URL}/locks/${websiteId}/acquire`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 409) {
          // Conflit - verrou déjà pris
          const lockError = new Error(data.message || 'Verrou déjà acquis') as LockError & Error;
          lockError.type = 'LOCK_EXISTS';
          lockError.lockedBy = data.lockedBy;
          lockError.lockedAt = data.lockedAt;
          lockError.expiresAt = data.expiresAt;
          throw lockError;
        }
        throw new Error(data.error || 'Erreur lors de l\'acquisition du verrou');
      }

      return data;
    } catch (error) {
      console.error('Erreur lors de l\'acquisition du verrou:', error);
      throw error;
    }
  }

  // Libérer un verrou
  async releaseLock(websiteId: string): Promise<LockResult> {
    try {
      const response = await fetch(`${API_BASE_URL}/locks/${websiteId}/release`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de la libération du verrou');
      }

      return data;
    } catch (error) {
      console.error('Erreur lors de la libération du verrou:', error);
      throw error;
    }
  }

  // Vérifier le statut d'un verrou
  async checkLockStatus(websiteId: string): Promise<LockStatus> {
    try {
      const response = await fetch(`${API_BASE_URL}/locks/${websiteId}/status`, {
        headers: authService.getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de la vérification du verrou');
      }

      return data;
    } catch (error) {
      console.error('Erreur lors de la vérification du verrou:', error);
      throw error;
    }
  }

  // Obtenir tous les verrous actifs
  async getActiveLocks(): Promise<Record<string, any>> {
    try {
      const response = await fetch(`${API_BASE_URL}/locks/active`, {
        headers: authService.getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de la récupération des verrous actifs');
      }

      return data.locks;
    } catch (error) {
      console.error('Erreur lors de la récupération des verrous actifs:', error);
      throw error;
    }
  }

  // Nettoyer les verrous expirés
  async cleanupExpiredLocks(): Promise<{ cleanedCount: number }> {
    try {
      const response = await fetch(`${API_BASE_URL}/locks/cleanup`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors du nettoyage des verrous');
      }

      return data;
    } catch (error) {
      console.error('Erreur lors du nettoyage des verrous:', error);
      throw error;
    }
  }
}

// Instance singleton
const lockService = new LockService();
export default lockService; 